import {
  Box,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Stepper,
  Table,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import { getOrderDetails } from "api/order.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_DOWNLOAD_TITLE,
} from "common/constants";
import { formatDate, printPage } from "common/utils";
import PreviewHeader from "components/header/PreviewHeader";
import { PRINT_MARGIN } from "components/layouts/consts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useEffect, useState } from "react";
import type { Order, OrderDetails } from "types/model.types";

interface IOrderPreviewProps {
  order: Order;
}
// The OrderPreview component is responsible for displaying the details of a specific order
// It fetches the order details from the API and renders them in a structured format
const OrderPreview = ({ order }: IOrderPreviewProps) => {
  const [orderDetails, setOrderDetails] = useState<OrderDetails>([]);
  const [exporting, setExporting] = useState(false);

  // Fetch order details when the component mounts
  // This effect runs once when the component is first rendered
  useEffect(() => {
    (async () => {
      try {
        const resp = await safeApiCall(getOrderDetails(order.orderId));
        setOrderDetails(resp?.data || []);
      } catch (error) {
        logError(error);
      }
    })();
  }, [order]);

  const exportToPDF = async () => {
    setExporting(true);
    const elementsToHide = document.querySelectorAll(".no-print");
    try {
      elementsToHide?.forEach((el) => {
        (el as HTMLElement).style.display = "none";
      });
      const element = document.getElementById("preview-content");
      if (!element) return;

      const canvas = await html2canvas(element);
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const width = pdf.internal.pageSize.getWidth();
      const usableWidth = width - PRINT_MARGIN * 2;
      const usableHeight = (canvas.height * usableWidth) / canvas.width;

      pdf.addImage(
        imgData,
        "PNG",
        PRINT_MARGIN,
        PRINT_MARGIN,
        usableWidth,
        usableHeight,
      );
      pdf.save(`Order_${order.orderId}.pdf`);

      notifications.show({
        title: MSG_DEFAULT_DOWNLOAD_TITLE,
        message: "Your order has been saved as a PDF.",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Success,
      });
    } catch (error) {
      notifications.show({
        title: MSG_DEFAULT_DOWNLOAD_TITLE,
        message: "Something went wrong during PDF export.",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      console.error(error);
    } finally {
      setExporting(false);
      elementsToHide?.forEach((el) => {
        (el as HTMLElement).style.display = "none";
      });
    }
  };
  const handlePrint = () => {
    const elementsToHide = document.querySelectorAll(".no-print");
    elementsToHide?.forEach((el) => {
      (el as HTMLElement).style.display = "none";
    });

    const preview = document.getElementById("preview-content");
    if (!preview) {
      return;
    }
    printPage(preview.innerHTML);

    elementsToHide?.forEach((el) => {
      (el as HTMLElement).style.display = "";
    });
  };

  return (
    <>
      {!exporting && (
        <Group justify="flex-end" mb="md" className="no-print">
          <Button onClick={handlePrint} variant="default">
            Print
          </Button>
          <Button
            onClick={exportToPDF}
            variant="outline"
            color="blue"
            disabled={exporting}
          >
            {exporting ? <Loader size="xs" /> : "Export PDF"}
          </Button>
        </Group>
      )}
      <Card withBorder shadow="sm" padding="lg" radius="md">
        <Stack
          id="preview-content"
          style={{
            width: "100%",
            minWidth: "794px", // A4 width in pixels
            margin: "0 auto", // center on screen
          }}
        >
          <PreviewHeader />
          <Box mb="md" className="no-print">
            <Stepper active={4} color="blue" size="md">
              <Stepper.Step label="Order" description="Web order created" />
              <Stepper.Step
                label="Processing"
                description="Order being fulfilled"
              />
              <Stepper.Step
                label="Processed"
                description="Order being packed"
              />
              <Stepper.Step label="Complete" description="Order Complete" />
            </Stepper>
          </Box>
          <Table
            style={{
              width: "100%",
              marginBottom: "1rem",
            }}
          >
            <Table.Tbody>
              <Table.Tr>
                <Table.Td style={{ width: "75%" }}>
                  <Text ta={"left"} size="lg" fw={500}>
                    Date Ordered: {formatDate(order.createdOn)}
                    <br />
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text ta={"right"} size="lg" fw={500}>
                    ORDER NO:
                    <br />
                    CUSTOMER NO:
                    <br />
                    GST:
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text ta={"right"} size="lg" fw={500}>
                    {order.orderId}
                    <br />
                    {order.invoiceToDebtorCode}
                    <br />
                    20-898-763
                  </Text>
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
          {/* Render logic for orderDetails array here */}
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Code</Table.Th>
                <Table.Th>Title</Table.Th>
                <Table.Th>Quantity Ordered</Table.Th>
                <Table.Th>Quantity Supplied</Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {orderDetails.map((detail) => (
                <Table.Tr key={detail.orderDetailId}>
                  <Table.Td>{detail.productCode}</Table.Td>
                  <Table.Td>{detail.productDesc}</Table.Td>
                  <Table.Td>{detail.ordered ?? 0}</Table.Td>
                  <Table.Td>{detail.supplied ?? 0}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      </Card>
    </>
  );
};

export default OrderPreview;
